import { BaseUser } from "../../user/base-user.model";
import { QuoteEmail } from "./email.model";
import { Client } from "../../client/client.model";
import { IRequestInfo } from "../../shared/request-info/request-info.interface";
import { Insurance } from "./insurance.model";
import { QuoteClientEmailDetails } from "./client-email-details.model";
export abstract class BaseQuote {
  public requestInfo: IRequestInfo;

  // Set by QuoteMapper after construction — the quote detail endpoint is the only
  // one that serializes it, and threading it through every subclass constructor
  // would mean touching all 13 positional arg lists.
  private clientEmailDetails: QuoteClientEmailDetails | null = null;

  constructor(
    protected id: number,
    protected displayId: string,
    protected groupId: number,
    protected ref: string,
    protected sender: BaseUser,
    protected createdBy: BaseUser,
    protected status: string,
    protected quoteType: string,
    protected clients: Client[],
    protected createdAt: Date,
    protected updatedAt: Date,
    protected updatedBy: BaseUser,
    protected openedAt: Date,
    protected validFrom: Date,
    protected validTo: Date,
    protected email: QuoteEmail,
    protected internalComment: string,
    protected customTerms: string,
    protected fileName: string,
    protected fileNameInternal: string,
    protected averageMargin: number,
    protected inquiry: string,
    protected source: string,
    protected insurance: Insurance | null,
    protected reasonOfDecline: string,
    protected input: string | null = null,
  ) {}

  public getId(): number {
    return this.id;
  }

  public setId(id: number) {
    this.id = id;
  }

  public getInsurance(): Insurance | null {
    return this.insurance;
  }

  public setInsurance(insurance: Insurance | null): void {
    this.insurance = insurance;
  }

  public getDisplayId(): string {
    return this.displayId;
  }

  public getGroupId(): number {
    return this.groupId;
  }

  public getRef(): string {
    return this.ref;
  }

  public setRef(ref: string) {
    this.ref = ref;
  }

  public getSender(): BaseUser {
    return this.sender;
  }

  public setSender(param: BaseUser) {
    this.sender = param;
  }

  public getUpdatedBy(): BaseUser {
    return this.updatedBy;
  }

  public setUpdatedBy(param: BaseUser) {
    this.updatedBy = param;
  }

  public getStatus(): string {
    return this.status;
  }

  public setStatus(status: string): void {
    this.status = status;
  }

  public getClients(): Client[] {
    return this.clients;
  }

  public get senderCreatorName(): string {
    if (this.sender && this.sender.getFirstName()) {
      return this.sender.getFirstName() + " " + this.sender.getLastName();
    }
    if (this.createdBy && this.createdBy.getFirstName()) {
      return this.createdBy.getFirstName() + " " + this.createdBy.getLastName();
    }

    return this.createdBy ? this.createdBy.getUsername() : "";
  }

  public get updatedBySenderCreatorName(): string {
    if (this.updatedBy && this.updatedBy.getFirstName()) {
      return this.updatedBy.getFirstName() + " " + this.updatedBy.getLastName();
    }
    return this.senderCreatorName;
  }

  public get clientNames(): string {
    const names = [];
    if (this.clients && this.clients.length) {
      for (let i = 0; i < this.clients.length; i++) {
        names.push(this.clients[i].getName());
      }
    }

    return names.join(", ");
  }

  public setClients(clients: Client[]): void {
    this.clients = clients;
  }

  public getCreatedAtDate(): Date {
    return this.createdAt;
  }

  public getValidFrom(): Date {
    return this.validFrom;
  }

  public setValidFrom(validFrom: Date) {
    this.validFrom = validFrom;
  }

  public getValidTo(): Date {
    return this.validTo;
  }

  public setValidTo(validTo: Date) {
    this.validTo = validTo;
  }

  public getQuoteType(): string {
    return this.quoteType;
  }

  public setQuoteType(quoteType: string) {
    this.quoteType = quoteType;
  }

  public getEmail(): QuoteEmail {
    return this.email;
  }

  public setEmail(email: QuoteEmail): void {
    this.email = email;
  }

  public setUpdatedAt(updatedAt: Date): void {
    this.updatedAt = updatedAt;
  }

  public getUpdatedAt(): Date {
    return this.updatedAt;
  }

  public getInternalComment(): string {
    return this.internalComment;
  }

  public setInternalComment(ic: string): void {
    this.internalComment = ic;
  }

  public getCustomTerms(): string {
    return this.customTerms;
  }
  public setCustomTerms(param: string) {
    this.customTerms = param;
  }

  public getFileName(): string {
    return this.fileName;
  }

  public getFileNameInternal(): string {
    return this.fileName;
  }

  public getAverageMargin(): number {
    return this.averageMargin || 0;
  }

  public setAverageMargin(param: number) {
    this.averageMargin = param;
  }

  public getInquiry(): string {
    return this.inquiry;
  }

  public getClientEmailDetails(): QuoteClientEmailDetails | null {
    return this.clientEmailDetails;
  }

  public setClientEmailDetails(details: QuoteClientEmailDetails | null): void {
    this.clientEmailDetails = details;
  }

  public getSource(): string {
    return this.source;
  }

  public isValid(): boolean {
    return this.clients && this.clients.length > 0;
  }

  public isSendable(): boolean {
    return this.isValid();
  }

  public getReasonOfDecline(): string {
    return this.reasonOfDecline;
  }

  public setReasonOfDecline(reason: string) {
    this.reasonOfDecline = reason;
  }

  public getInput(): string | null {
    return this.input;
  }

  public setInput(input: string | null): void {
    this.input = input;
  }
}
